import React, { useCallback, useContext, useEffect, useState } from "react";
import { CircularProgress, Drawer, useMediaQuery } from "@material-ui/core";
import ChevronRight from "@material-ui/icons/ChevronRight";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import Popover from "Components/Atoms/Popover";
import Button from "Components/Atoms/Button";
import Typography from "Components/Atoms/Typography";
import { GuestSearchResult } from "api/GuestBook/searchGuests";
import { dateHelper } from "utils/helper";
import { ReservationContext } from "Contexts/ReservationContext";
import { ReservationSearchState } from "CustomHooks/useReservationSearch";
import {
    GuestReservation,
    groupGuestReservations,
    guestReservationPath,
    loadGuestReservations,
    reservationDay
} from "./guestReservations";
import "./styles.scss";

type Props = {
    restaurantId: string;
    dialogId: string;
    guest: GuestSearchResult;
    anchorEl: HTMLElement;
    search: string;
    onClose: (restoreFocus?: boolean) => void;
    containsTrigger: (target: EventTarget | null) => boolean;
};

const GuestReservationWindow = ({
    restaurantId,
    dialogId,
    guest,
    anchorEl,
    search,
    onClose,
    containsTrigger
}: Props) => {
    const { t, i18n } = useTranslation("reservations");
    const history = useHistory();
    const { setcurrentReservation, setcurrentTable, setcurrentWaitinglistEntry } = useContext(ReservationContext);
    const location = useLocation<ReservationSearchState>();
    const mobile = useMediaQuery("(max-width:600px)");
    const focusDialog = useCallback(
        (element: HTMLDivElement | null) => {
            if (!mobile) element?.focus();
        },
        [mobile]
    );
    const [retry, setRetry] = useState(0);
    const [visibleCount, setVisibleCount] = useState(6);
    const [result, setResult] = useState<{
        reservations: GuestReservation[];
        failed: boolean;
    } | null>(null);

    useEffect(() => {
        let cancelled = false;
        setResult(null);
        loadGuestReservations(restaurantId, guest.id)
            .then((reservations) => {
                if (!cancelled) setResult({ reservations, failed: false });
            })
            .catch(() => {
                if (!cancelled) setResult({ reservations: [], failed: true });
            });
        return () => {
            cancelled = true;
        };
    }, [restaurantId, guest.id, retry]);

    useEffect(() => {
        if (mobile) return;
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onClose(true);
            }
        };
        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [mobile, onClose]);

    const openReservation = (reservation: GuestReservation) => {
        const path = guestReservationPath(
            restaurantId,
            reservation,
            location.pathname
        );
        if (!path) return;
        const day = reservationDay(reservation.date);
        history.replace({
            ...location,
            state: {
                ...location.state,
                reservationGuestSearch: {
                    restaurantId,
                    date:
                        reservationDay(location.pathname.split("/")[3]) ||
                        dateHelper(),
                    search
                }
            }
        });
        history.push(path, {
            reservationGuestSearch: { restaurantId, date: day, search }
        });
        setcurrentTable(null);
        setcurrentWaitinglistEntry(null);
        setcurrentReservation(reservation.id);
        onClose(false);
    };

    const status = (reservation: GuestReservation) =>
        reservation.status === "failed"
            ? "Canceled"
            : reservation.done
            ? "Guest reservation finished"
            : reservation.started
            ? "Guest reservation seated"
            : ["success", "positive"].includes(reservation.status)
            ? "Guest reservation confirmed"
            : "Guest reservation pending";
    const dateLabel = (value: string) =>
        new Intl.DateTimeFormat(i18n.language || "de", {
            weekday: "short",
            day: "numeric",
            month: "long",
            year: "numeric"
        }).format(
            new Date((reservationDay(value) || dateHelper()) + "T12:00:00")
        );
    let remaining = visibleCount;
    const groups = groupGuestReservations(
        result?.reservations || [],
        dateHelper()
    ).map((group) => {
        const reservations = group.reservations.slice(0, remaining);
        remaining -= reservations.length;
        return { ...group, reservations };
    });
    const content = (
        <div
            id={dialogId}
            className="reservation-guest-window"
            ref={focusDialog}
            tabIndex={-1}
            role="dialog"
            aria-modal={mobile}
            aria-label={t("Reservations of guest", { name: guest.name })}
            onClick={(event) => event.stopPropagation()}
        >
            <header className="reservation-guest-header">
                <div className="reservation-guest-heading">
                    <Typography component="h2" variant="h5">
                        {guest.name}
                    </Typography>
                    <Button variant="transparent" onClick={() => onClose(true)}>
                        {t("Close guest window")}
                    </Button>
                </div>
                {guest.email && (
                    <div className="reservation-guest-contact">
                        <span>{t("Guest search email")}</span>
                        <span>{guest.email}</span>
                    </div>
                )}
                {guest.phone && (
                    <div className="reservation-guest-contact">
                        <span>{t("Guest search phone")}</span>
                        <span>{guest.phone}</span>
                    </div>
                )}
                <div className="reservation-guest-attributes">
                    {guest.attr
                        ?.filter((attribute) =>
                            ["vip", "regular", "blacklist"].includes(attribute)
                        )
                        .map((attribute) => (
                            <span
                                key={attribute}
                                className={`reservation-guest-attribute ${
                                    attribute === "blacklist" ? "critical" : ""
                                }`}
                            >
                                {t(`Guest search ${attribute}`)}
                            </span>
                        ))}
                    {typeof guest.visits === "number" && (
                        <span>
                            {t("Guest reservation count", {
                                count: guest.visits
                            })}
                        </span>
                    )}
                </div>
            </header>
            <section
                className="reservation-guest-history"
                aria-live="polite"
                aria-busy={!result}
            >
                <Typography component="h3" variant="text-3">
                    {t("Guest reservations heading")}
                </Typography>
                {!result ? (
                    <div className="reservation-guest-state">
                        <CircularProgress size={18} />
                        <span>{t("Loading guest reservations")}</span>
                    </div>
                ) : result.failed ? (
                    <div className="reservation-guest-state">
                        <span>{t("Guest reservations unavailable")}</span>
                        <Button
                            variant="transparent"
                            onClick={() => setRetry((value) => value + 1)}
                        >
                            {t("Retry guest search")}
                        </Button>
                    </div>
                ) : !result.reservations.length ? (
                    <div className="reservation-guest-state">
                        {t("No linked guest reservations")}
                    </div>
                ) : (
                    groups.map(
                        (group) =>
                            group.reservations.length > 0 && (
                                <div key={group.title}>
                                    <div className="reservation-guest-group">
                                        {t(group.title)}
                                    </div>
                                    {group.reservations.map((reservation) => (
                                        <Button
                                            key={reservation.id}
                                            variant="transparent"
                                            fullWidth
                                            className="reservation-guest-reservation"
                                            onClick={() =>
                                                openReservation(reservation)
                                            }
                                            aria-label={t(
                                                "Open guest reservation",
                                                {
                                                    date: dateLabel(
                                                        reservation.date
                                                    ),
                                                    time: reservation.time
                                                }
                                            )}
                                        >
                                            <span className="reservation-guest-reservation-info">
                                                <span className="reservation-guest-reservation-date">
                                                    {dateLabel(
                                                        reservation.date
                                                    )}
                                                </span>
                                                <span className="reservation-guest-reservation-meta">
                                                    {reservation.time} ·{" "}
                                                    {t(
                                                        "Guest reservation people",
                                                        {
                                                            count: reservation.guests
                                                        }
                                                    )}
                                                    {reservation.tables &&
                                                        reservation.tables
                                                            .length > 0 && (
                                                            <>
                                                                {" "}
                                                                ·{" "}
                                                                {t(
                                                                    "Guest reservation table"
                                                                )}{" "}
                                                                {reservation.tables.join(
                                                                    ", "
                                                                )}
                                                            </>
                                                        )}
                                                </span>
                                                <span
                                                    className={`reservation-guest-status ${
                                                        reservation.status ===
                                                        "failed"
                                                            ? "critical"
                                                            : ""
                                                    }`}
                                                >
                                                    {t(status(reservation))}
                                                </span>
                                            </span>
                                            <ChevronRight fontSize="small" />
                                        </Button>
                                    ))}
                                </div>
                            )
                    )
                )}
                {result &&
                    !result.failed &&
                    result.reservations.length > visibleCount && (
                        <Button
                            variant="transparent"
                            fullWidth
                            className="reservation-guest-more"
                            onClick={() =>
                                setVisibleCount((value) => value + 6)
                            }
                        >
                            {t("More guest reservations")}
                        </Button>
                    )}
            </section>
        </div>
    );

    if (mobile)
        return (
            <Drawer
                anchor="bottom"
                open
                onClose={() => onClose(false)}
                classes={{ paper: "reservation-guest-drawer" }}
            >
                {content}
            </Drawer>
        );
    return (
        <Popover
            open
            anchorEl={anchorEl}
            placement="right-start"
            width={416}
            padding={0}
            className="popover reservation-guest-popover"
            popperProps={{
                role: "presentation",
                modifiers: {
                    offset: { enabled: true, offset: "0,12" },
                    preventOverflow: {
                        enabled: true,
                        boundariesElement: "viewport",
                        padding: 16
                    }
                }
            }}
            style={{ marginTop: 0 }}
            onClose={(event) => {
                if (!containsTrigger(event.target)) onClose(false);
            }}
        >
            {content}
        </Popover>
    );
};

export default GuestReservationWindow;
